import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    IconButton,
    Button,
    Typography,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    DialogContentText,
    CircularProgress,
    Tooltip,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    List,
    ListItem,
    Divider,
    Alert,
    TextField,
} from '@mui/material';
import {
    Database,
    FileText,
    Edit,
    Trash2,
    Share2,
    Upload,
    Eye,
    X,
    AlertCircle,
    MoreVertical
} from 'lucide-react';
import { repositoriesApi } from '../../../services/api';
import ShareDialog from '../../common/ShareDialog';
import { ENTITY_TYPES } from '../../../services/shareService';

const RepositoryCard = ({ repository, token, setError, onUpdate }) => {
    const { t } = useTranslation();

    // Состояния
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [viewMaterialsDialogOpen, setViewMaterialsDialogOpen] = useState(false);
    const [uploadMaterialDialogOpen, setUploadMaterialDialogOpen] = useState(false);
    const [editRepositoryDialogOpen, setEditRepositoryDialogOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Состояния для загрузки файлов
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadError, setUploadError] = useState('');

    // Состояния для редактирования репозитория
    const [editFormData, setEditFormData] = useState({
        name: repository.name || '',
        description: repository.description || ''
    });
    const [isEditing, setIsEditing] = useState(false);
    const [editError, setEditError] = useState('');

    // Обновление данных формы редактирования при изменении репозитория
    useEffect(() => {
        setEditFormData({
            name: repository.name || '',
            description: repository.description || ''
        });
    }, [repository]);

    // Обработчик удаления репозитория
    const handleDeleteRepository = async () => {
        try {
            setIsDeleting(true);
            await repositoriesApi.deleteMaterialRepository(repository.id, token);
            setIsDeleting(false);
            setDeleteDialogOpen(false);

            if (onUpdate) {
                onUpdate();
            }
        } catch (err) {
            setIsDeleting(false);
            setError(t('dashboard.ai_tutors.error.deleting_repository'));
            
        }
    };

    const handleMenuOpen = (event) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    // Константы для загрузки файлов
    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 МБ
    const ALLOWED_FILE_TYPES = ['text/plain', 'application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/zip'];
    const ALLOWED_FILE_EXTENSIONS = ['.txt', '.pdf', '.docx', '.zip'];

    // Обработчик выбора файла для загрузки
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setUploadError('');

        if (!file) return;

        // Проверка типа файла
        const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        const isAllowedType = ALLOWED_FILE_TYPES.includes(file.type) ||
            ALLOWED_FILE_EXTENSIONS.includes(fileExtension);

        if (!isAllowedType) {
            setUploadError(t('dashboard.ai_tutors.error.file_type'));
            setSelectedFile(null);
            return;
        }

        // Проверка размера файла
        if (file.size > MAX_FILE_SIZE) {
            setUploadError(t('dashboard.ai_tutors.error.file_too_large'));
            setSelectedFile(null);
            return;
        }

        setSelectedFile(file);
    };

    // Обработчик загрузки файла
    const handleUploadFile = async () => {
        if (!selectedFile) return;

        setIsUploading(true);
        setUploadProgress(0);
        setUploadError('');

        try {
            // Имитируем прогресс загрузки
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return 90;
                    }
                    return prev + 10;
                });
            }, 300);

            const formData = new FormData();
            formData.append('file', selectedFile);
            await repositoriesApi.uploadRepositoryMaterial(repository.id, formData, token);

            clearInterval(progressInterval);
            setUploadProgress(100);

            // Сбрасываем выбранный файл
            setSelectedFile(null);

            // Закрываем диалог после успешной загрузки
            setTimeout(() => {
                setIsUploading(false);
                setUploadProgress(0);
                setUploadMaterialDialogOpen(false);

                // В реальном приложении здесь должно быть обновление данных
                if (onUpdate) {
                    onUpdate();
                }
            }, 500);

        } catch (err) {
            
            setUploadError(t('dashboard.ai_tutors.error.uploading'));
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    // Форматирование размера файла
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Обработчик изменения полей формы редактирования
    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Валидация формы редактирования
    const validateEditForm = () => {
        if (!editFormData.name.trim()) {
            setEditError(t('dashboard.ai_tutors.error.repository_name_required'));
            return false;
        }
        return true;
    };

    // Обработчик сохранения изменений репозитория
    const handleUpdateRepository = async () => {
        if (!validateEditForm()) return;

        setIsEditing(true);
        setEditError('');

        try {
            // В реальном приложении здесь должен быть запрос на обновление репозитория
            // Например: await updateMaterialRepository(token, repository.id, editFormData);

            // Имитируем задержку обновления
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Закрываем диалог после успешного обновления
            setIsEditing(false);
            setEditRepositoryDialogOpen(false);

            // В реальном приложении здесь должно быть обновление данных
            if (onUpdate) {
                onUpdate();
            }
        } catch (err) {
            
            setEditError(t('dashboard.ai_tutors.error.updating'));
            setIsEditing(false);
        }
    };

    return (
        <div className="bg-white p-4 rounded-lg border border-gray-200 hover:border-blue-300 transition-all shadow-sm">
            <div className="flex justify-between items-start mb-3">
                <div className="flex-grow">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2 mb-2">
                        <Database className="text-blue-500 w-5 h-5" />
                        {repository.name}
                    </h3>

                    <div className="flex flex-wrap items-center gap-2">
                        <Chip
                            label={t('dashboard.ai_tutors.materials_count', { count: repository.materials_count || 0 })}
                            size="small"
                            className="bg-gray-100 text-gray-700"
                            icon={<FileText className="w-3.5 h-3.5" />}
                        />

                        <Chip
                            label={repository.owner_username}
                            size="small"
                            className="bg-blue-50 text-blue-700"
                        />
                    </div>
                </div>

                <div className="flex items-center">
                    <Tooltip title={t('dashboard.ai_tutors.repository_dialog.actions')}>
                        <IconButton
                            onClick={handleMenuOpen}
                            size="small"
                            className="text-gray-500"
                        >
                            <MoreVertical className="w-4 h-4" />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>

            {/* Описание репозитория */}
            {repository.description && (
                <div className="mb-3">
                    <Typography className="text-sm text-gray-600 line-clamp-2">
                        {repository.description.substring(0, 120)}
                        {repository.description.length > 120 ? '...' : ''}
                    </Typography>
                </div>
            )}

            {/* Меню действий */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => {
                    handleMenuClose();
                    setViewMaterialsDialogOpen(true);
                }}>
                    <ListItemIcon>
                        <Eye className="w-4 h-4" />
                    </ListItemIcon>
                    <ListItemText>
                        {t('dashboard.ai_tutors.repository_dialog.view_materials')}
                    </ListItemText>
                </MenuItem>

                <MenuItem onClick={() => {
                    handleMenuClose();
                    setUploadMaterialDialogOpen(true);
                }}>
                    <ListItemIcon>
                        <Upload className="w-4 h-4" />
                    </ListItemIcon>
                    <ListItemText>
                        {t('dashboard.ai_tutors.repository_dialog.upload_material')}
                    </ListItemText>
                </MenuItem>

                <MenuItem onClick={() => {
                    handleMenuClose();
                    setShareDialogOpen(true);
                }}>
                    <ListItemIcon>
                        <Share2 className="w-4 h-4" />
                    </ListItemIcon>
                    <ListItemText>
                        {t('dashboard.ai_tutors.repository_dialog.share')}
                    </ListItemText>
                </MenuItem>

                <MenuItem onClick={() => {
                    handleMenuClose();
                    setEditRepositoryDialogOpen(true);
                }}>
                    <ListItemIcon>
                        <Edit className="w-4 h-4" />
                    </ListItemIcon>
                    <ListItemText>
                        {t('dashboard.ai_tutors.repository_dialog.edit')}
                    </ListItemText>
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        handleMenuClose();
                        setDeleteDialogOpen(true);
                    }}
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon>
                        <Trash2 className="w-4 h-4 text-red-600" />
                    </ListItemIcon>
                    <ListItemText>
                        {t('dashboard.ai_tutors.repository_dialog.delete')}
                    </ListItemText>
                </MenuItem>
            </Menu>

            {/* Диалог подтверждения удаления */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => !isDeleting && setDeleteDialogOpen(false)}
            >
                <DialogTitle className="text-red-600">
                    {t('dashboard.ai_tutors.repository_dialog.delete_title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('dashboard.ai_tutors.repository_dialog.delete_message', { name: repository.name })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        disabled={isDeleting}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleDeleteRepository}
                        color="error"
                        variant="contained"
                        disabled={isDeleting}
                    >
                        {isDeleting ? (
                            <>
                                <CircularProgress size={20} className="mr-2" />
                                {t('dashboard.ai_tutors.repository_dialog.deleting')}
                            </>
                        ) : (
                            t('common.delete')
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог шаринга */}
            {shareDialogOpen && (
                <ShareDialog
                    open={shareDialogOpen}
                    onClose={() => setShareDialogOpen(false)}
                    entity={repository}
                    entityType={ENTITY_TYPES.AI_TUTOR} // Используем AI_TUTOR пока нет REPOSITORY
                    token={token}
                    onError={setError}
                    onSuccess={() => setShareDialogOpen(false)}
                />
            )}

            {/* Диалог просмотра материалов */}
            <Dialog
                open={viewMaterialsDialogOpen}
                onClose={() => setViewMaterialsDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FileText className="text-blue-500" />
                        {t('dashboard.ai_tutors.repository_dialog.view_materials')}
                    </div>
                    <IconButton onClick={() => setViewMaterialsDialogOpen(false)} size="small">
                        <X className="w-5 h-5" />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle1" className="mb-4">
                        {repository.name} - {t('dashboard.ai_tutors.materials_count', { count: repository.materials_count || 0 })}
                    </Typography>

                    {repository.materials_count === 0 ? (
                        <Alert severity="info">
                            {t('dashboard.ai_tutors.details_dialog.no_materials')}
                        </Alert>
                    ) : (
                        <List>
                            {/* В идеале здесь должен быть запрос на получение материалов репозитория */}
                            {/* Но пока просто показываем заглушку */}
                            <ListItem divider>
                                <ListItemIcon>
                                    <FileText className="text-gray-500" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="example.txt"
                                    secondary={
                                        <Typography variant="body2" color="textSecondary">
                                            {new Date().toLocaleDateString()}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewMaterialsDialogOpen(false)}>
                        {t('common.close')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог загрузки материала */}
            <Dialog
                open={uploadMaterialDialogOpen}
                onClose={() => !isUploading && setUploadMaterialDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Upload className="text-blue-500" />
                        {t('dashboard.ai_tutors.repository_dialog.upload_material')}
                    </div>
                    <IconButton 
                        onClick={() => !isUploading && setUploadMaterialDialogOpen(false)} 
                        size="small"
                        disabled={isUploading}
                    >
                        <X className="w-5 h-5" />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle1" className="mb-4">
                        {repository.name}
                    </Typography>

                    <div className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex flex-col gap-3">
                            <div className="flex items-center gap-2">
                                <input
                                    type="file"
                                    id="material-upload"
                                    className="hidden"
                                    onChange={handleFileChange}
                                    accept=".txt,.pdf,.docx,.zip"
                                    disabled={isUploading}
                                />
                                <Button
                                    variant="outlined"
                                    component="label"
                                    htmlFor="material-upload"
                                    disabled={isUploading}
                                    startIcon={<Upload className="w-4 h-4" />}
                                >
                                    {t('dashboard.ai_tutors.details_dialog.select_file')}
                                </Button>
                                {selectedFile && (
                                    <Typography variant="body2" className="ml-2">
                                        {selectedFile.name} ({formatFileSize(selectedFile.size)})
                                    </Typography>
                                )}
                            </div>

                            {uploadError && (
                                <Alert severity="error" className="mt-2">
                                    {uploadError}
                                </Alert>
                            )}

                            <Divider className="my-2" />

                            <Typography variant="body2" color="textSecondary">
                                {t('dashboard.ai_tutors.details_dialog.file_requirements')}
                            </Typography>

                            <div className="flex items-center gap-2">
                                <Chip
                                    label={t('dashboard.ai_tutors.details_dialog.max_file_size', { size: '50 MB' })}
                                    size="small"
                                    icon={<AlertCircle className="w-3.5 h-3.5" />}
                                />
                                <Chip
                                    label={t('dashboard.ai_tutors.details_dialog.allowed_types', { types: '.txt, .pdf, .docx, .zip' })}
                                    size="small"
                                    icon={<AlertCircle className="w-3.5 h-3.5" />}
                                />
                            </div>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => !isUploading && setUploadMaterialDialogOpen(false)}
                        disabled={isUploading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleUploadFile}
                        variant="contained"
                        color="primary"
                        disabled={!selectedFile || isUploading}
                    >
                        {isUploading ? (
                            <div className="flex items-center gap-2">
                                <CircularProgress size={20} color="inherit" />
                                {t('dashboard.ai_tutors.details_dialog.uploading')} {uploadProgress}%
                            </div>
                        ) : (
                            t('dashboard.ai_tutors.details_dialog.upload')
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог редактирования репозитория */}
            <Dialog
                open={editRepositoryDialogOpen}
                onClose={() => !isEditing && setEditRepositoryDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Edit className="text-blue-500" />
                        {t('dashboard.ai_tutors.repository_dialog.edit')}
                    </div>
                    <IconButton 
                        onClick={() => !isEditing && setEditRepositoryDialogOpen(false)} 
                        size="small"
                        disabled={isEditing}
                    >
                        <X className="w-5 h-5" />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {editError && (
                        <Alert severity="error" className="mb-4">
                            {editError}
                        </Alert>
                    )}

                    <div className="space-y-4 mt-2">
                        {/* Название репозитория */}
                        <TextField
                            name="name"
                            label={t('dashboard.ai_tutors.repository_dialog.name')}
                            value={editFormData.name}
                            onChange={handleEditInputChange}
                            fullWidth
                            required
                            disabled={isEditing}
                        />

                        {/* Описание репозитория */}
                        <TextField
                            name="description"
                            label={t('dashboard.ai_tutors.repository_dialog.description')}
                            value={editFormData.description}
                            onChange={handleEditInputChange}
                            multiline
                            rows={4}
                            fullWidth
                            disabled={isEditing}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => !isEditing && setEditRepositoryDialogOpen(false)}
                        disabled={isEditing}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleUpdateRepository}
                        variant="contained"
                        color="primary"
                        disabled={!editFormData.name.trim() || isEditing}
                    >
                        {isEditing ? (
                            <div className="flex items-center gap-2">
                                <CircularProgress size={20} color="inherit" />
                                {t('dashboard.ai_tutors.details_dialog.saving')}
                            </div>
                        ) : (
                            t('dashboard.ai_tutors.details_dialog.save')
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default RepositoryCard;
